import { Injectable } from '@angular/core';
import { EMPTY, interval, of, Subscription } from 'rxjs';
import { catchError, switchMap, tap, startWith } from 'rxjs/operators';
import { Router } from '@angular/router';
import { TokenFacade } from '../token-facade.service';
import { UserGraphqlService } from '../../api/graphql/user/user-graphql.service';
import { IsAdminService } from '../admin/is-admin.service';
import { Route, RouterLinks } from '../application-routes.service';
import { JwtPayload } from '../tokenKeeper';
import { DismissModalHelperService } from '../dismiss-modal-helper.service';
import { WindowService } from '../window.service';

const ONE_MINUITE_IN_MILLISECONDS = 60 * 1000;
const THIRTY_MINUITES_IN_SECONDS = 60 * 30;

@Injectable({
  providedIn: 'root',
})
export class TokenRefreshService {
  constructor(
    private tokenFacade: TokenFacade,
    private userGraphQlService: UserGraphqlService,
    private router: Router,
    private isAdminService: IsAdminService,
    private dismissModalHelperService: DismissModalHelperService,
    private windowService: WindowService,
  ) {}

  private unsubscribeInterval?: Subscription = this.tokenFacade.tokenPayload$
    .pipe(
      switchMap(() => interval(ONE_MINUITE_IN_MILLISECONDS)),
      startWith(-1),
      switchMap(() => this.renew()),
      catchError(() => {
        const isOktaSignIn = this.tokenFacade
          .getPayloadFromLocalStorage()
          .isOktaToken();
        this.tokenFacade.clearToken();
        this.stopRefresh();

        // If a navigate fires on angulars first change detection cycle
        // it eats the NaivgationStart event
        setTimeout(() => {
          let signInPath = RouterLinks.SIGN_IN_USERNAME_PASSWORD;
          if (isOktaSignIn) {
            signInPath = RouterLinks.SIGN_IN_OKTA;
          }
          if (this.isAdminService.isAdmin) {
            this.windowService.window.location.href = Route.ADMIN_SIGN_IN;
          }
          this.router.navigateByUrl(signInPath);
        });
        return EMPTY;
      }),
    )
    .subscribe();

  stopRefresh() {
    this.unsubscribeInterval?.unsubscribe();
  }

  renew() {
    const currentToken = this.tokenFacade.getToken();
    if (!currentToken) return of();
    const payload = this.tokenFacade.getPayloadFromLocalStorage();
    if (payload.isv1token()) {
      return EMPTY;
    }

    if (payload.isTokenExpired()) {
      console.warn(
        `Token is expired timeToExpirationInSeconds: ${payload.timeToExpirationInSeconds()}`,
      );
      throw new Error('Token is expired');
    }

    if (this.isTokenAboutToExpire(payload)) {
      return this.callRefreshTokenApi(currentToken);
    }

    if (payload.isProspectiveSeller() && payload.isNonAptitudeUser()) {
      return this.callRefreshTokenApi(currentToken).pipe(
        switchMap(() => this.tokenFacade.tokenPayload$),
        tap((newTokenPayload) =>
          this.handleBecomingAptitudeUser(newTokenPayload),
        ),
      );
    }

    return of(currentToken);
  }

  private isTokenAboutToExpire(token: JwtPayload) {
    return token.timeToExpirationInSeconds() < THIRTY_MINUITES_IN_SECONDS;
  }

  private callRefreshTokenApi(currentToken: string) {
    return this.userGraphQlService
      .refreshAptitudeToken(currentToken)
      .pipe(tap((token) => this.tokenFacade.setToken(token)));
  }

  private handleBecomingAptitudeUser(newTokenPayload: JwtPayload) {
    if (newTokenPayload.isAptitudeUser()) {
      this.dismissModalHelperService
        .openModal({
          confirmLabel: 'Reload',
          message: 'We detected a change in your permissions',
          subMessage:
            'Please reload your browser to reflect your newest permissions',
        })
        .finally(() => this.windowService.window.location.reload());
    }
  }
}
